/**
 * API Service - Axios Client für DEXIOS Backend
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request Interceptor für Auth-Token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor (für Error Handling)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ==================== PUBLIC API ====================

export const getPublicOverview = () => {
  return api.get('/public/overview');
};

export const getEndpointUptime = (endpointId, days = 90) => {
  return api.get(`/public/endpoint/${endpointId}/uptime`, {
    params: { days },
  });
};

export const getRecentChecks = (endpointId, limit = 50) => {
  return api.get(`/public/endpoint/${endpointId}/recent-checks`, {
    params: { limit },
  });
};

export const getEndpointDetails = (endpointId, days = 30) => {
  return api.get(`/public/endpoint/${endpointId}/details`, {
    params: { days },
  });
};

// ==================== STATUS API ====================

export const getStatusSummary = () => {
  return api.get('/status/summary');
};

export const getRecentIncidents = (hours = 24) => {
  return api.get('/status/incidents', {
    params: { hours },
  });
};

// ==================== ADMIN API ====================

export const listEndpoints = (params = {}) => {
  return api.get('/admin/endpoints', { params });
};

export const createEndpoint = (data) => {
  return api.post('/admin/endpoints', data);
};

export const getEndpoint = (endpointId) => {
  return api.get(`/admin/endpoints/${endpointId}`);
};

export const updateEndpoint = (endpointId, data) => {
  return api.put(`/admin/endpoints/${endpointId}`, data);
};

export const deleteEndpoint = (endpointId) => {
  return api.delete(`/admin/endpoints/${endpointId}`);
};

// ==================== ALERT CONFIGS ====================

export const listAlertConfigs = (endpointId) => {
  return api.get(`/admin/endpoints/${endpointId}/alerts`);
};

export const createAlertConfig = (endpointId, data) => {
  return api.post(`/admin/endpoints/${endpointId}/alerts`, data);
};

export const updateAlertConfig = (alertId, data) => {
  return api.put(`/admin/alerts/${alertId}`, data);
};

export const deleteAlertConfig = (alertId) => {
  return api.delete(`/admin/alerts/${alertId}`);
};

// ==================== SYSTEM MONITORING ====================

export const getSystemHealth = () => {
  return api.get('/system/health');
};

export const getSystemMetrics = () => {
  return api.get('/system/metrics');
};

export const getServiceLogs = (service, lines = 100) => {
  return api.get(`/system/logs/${service}`, {
    params: { lines },
  });
};

export const getRecentErrors = (hours = 24) => {
  return api.get('/system/recent-errors', {
    params: { hours },
  });
};

// ==================== SECURITY SCANNER API ====================

// CVE Scanner
export const scanCVE = (data) => {
  return api.post('/security/cve/scan', data).then(res => res.data);
};

export const getCVEScans = (params = {}) => {
  return api.get('/security/cve/scans', { params }).then(res => res.data);
};

export const getCVEScanDetails = (scanId) => {
  return api.get(`/security/cve/scans/${scanId}`).then(res => res.data);
};

export const downloadCVEReport = async (scanId) => {
  const response = await api.get(`/security/cve/scans/${scanId}/pdf`, {
    responseType: 'blob'
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `cve_scan_${scanId}_report.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

// Penetration Testing
export const scanPenTest = (data) => {
  return api.post('/security/pentest/scan', data).then(res => res.data);
};

export const getPenTestScans = (params = {}) => {
  return api.get('/security/pentest/scans', { params }).then(res => res.data);
};

export const getPenTestScanDetails = (scanId) => {
  return api.get(`/security/pentest/scans/${scanId}`).then(res => res.data);
};

export const downloadPenTestReport = async (scanId) => {
  const response = await api.get(`/security/pentest/scans/${scanId}/pdf`, {
    responseType: 'blob'
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `pentest_scan_${scanId}_report.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const getSecurityDashboardStats = () => {
  return api.get('/security/dashboard/stats').then(res => res.data);
};

export default api;
